import { MouseEvent, PropsWithChildren, ReactNode } from 'react'
import Dropzone, { DropzoneProps, FileRejection } from 'react-dropzone'
import { useIntl } from 'react-intl'
import { mdiCloudUploadOutline } from '@mdi/js'

import BaseModal from '../BaseModal'
import { ModalProps } from '../types'
import { H2 } from 'components/Typography'
import { dropZoneModalMessages } from './messages'
import { DEFAULT_MAX_FILE_SIZE } from './consts'
import {
  DropzoneModalContainer,
  DropzoneModalTitle,
  DropzoneModalBody,
  DropzoneModalDesc,
  DropzoneModalIconWrapper,
  DropzoneModalIcon,
  DropzoneButtonContainer,
} from './styles'
import { CommonButton } from 'components/Button'
import formatBytes from 'app/utils/formatBytes'

interface DropzoneModalProps extends ModalProps {
  isOpen: boolean
  title: ReactNode
  accept?: string | string[]
  disabled?: boolean
  multiple?: boolean
  maxFiles?: number
  maxSize?: number
  description: ReactNode
  onClose?: (ev: MouseEvent<HTMLElement>) => void
  handleFiles: (acceptedFiles: File[], fileRejections: FileRejection[]) => void
  validator?: DropzoneProps['validator']
}

function DropzoneModal({
  isOpen,
  title,
  children,
  accept,
  disabled,
  multiple = false,
  maxFiles = 1,
  maxSize = DEFAULT_MAX_FILE_SIZE,
  description,
  onClose,
  handleFiles,
  validator,
  onRequestClose = onClose,
}: PropsWithChildren<DropzoneModalProps>) {
  const { formatMessage } = useIntl()

  return (
    <BaseModal isOpen={isOpen} onRequestClose={onRequestClose}>
      <DropzoneModalContainer>
        <DropzoneModalTitle>
          <H2>{title}</H2>
        </DropzoneModalTitle>
        <Dropzone
          onDrop={(acceptedFiles, fileRejections) =>
            handleFiles(acceptedFiles, fileRejections)
          }
          validator={validator}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          maxFiles={maxFiles}
          maxSize={maxSize}
        >
          {({
            getRootProps,
            getInputProps,
            isDragActive,
            isDragAccept,
            isDragReject,
            fileRejections,
          }) => (
            <DropzoneModalBody
              {...getRootProps()}
              isDragActive={isDragActive}
              isDragReject={isDragReject}
            >
              <input {...getInputProps()} />
              <DropzoneModalIconWrapper>
                <DropzoneModalIcon path={mdiCloudUploadOutline} />
              </DropzoneModalIconWrapper>
              <DropzoneModalDesc
                isDragReject={
                  !isDragAccept && (isDragReject || !!fileRejections.length)
                }
              >
                {!isDragAccept && (isDragReject || !!fileRejections.length)
                  ? formatMessage(dropZoneModalMessages.errorMessage, {
                      accept: Array.isArray(accept)
                        ? accept.join(', ')
                        : accept,
                      maxSize: formatBytes(maxSize),
                      br: <br />,
                    })
                  : description}
              </DropzoneModalDesc>

              {children}
            </DropzoneModalBody>
          )}
        </Dropzone>
        <DropzoneButtonContainer>
          <CommonButton variant="secondary" size="big" onClick={onClose}>
            {formatMessage(dropZoneModalMessages.cancelLabel)}
          </CommonButton>
        </DropzoneButtonContainer>
      </DropzoneModalContainer>
    </BaseModal>
  )
}

export default DropzoneModal
